import {
  Box,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { Theme, useTheme } from "@mui/material/styles";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import { makeStyles } from "helpers/StyleHelper";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTitle from "hooks/useTitle";
import { useEffect } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Navigate, useParams } from "react-router-dom";
import {
  ResponseOfSeries,
  SavePriorRequirementRequiredRequest,
} from "services/KnowfullyAdminClient";
import { Mode } from "types/common-types";
import ModulesSection from "./components/ModulesSection";

const useStyles = makeStyles((theme: Theme) => ({
  tableTitle: {
    margin: "20px 0",
  },
}));

const SeriesUpdateForm = () => {
  const queryClient = useQueryClient();
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const { id } = useParams<{ id: string }>();
  const { seriesService, seriesUrl, seriesDetailsUrl } = useGlobalContext();
  const [checked, checkedActions] = useBoolean(false);
  const seriesResult = useQuery<ResponseOfSeries>(
    ["seriesById", id],
    () => seriesService.getSeriesById(id!),
    {
      enabled: !!id,
    }
  );

  const series = seriesResult.data?.data;
  const title = series?.seriesTitle || "";
  useTitle(title);

  useEffect(() => {
    if (series?.priorRequirementRequired) {
      checkedActions.setValue(series.priorRequirementRequired);
    }
  }, [series, checkedActions]);

  if (!id) return <Navigate to={seriesUrl()} replace />;
  if (seriesResult.isLoading) return <Loader />;
  if (seriesResult.isError) {
    console.log(seriesResult.error);
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="seriesLoadError" />
        </Alert>
      </Box>
    );
  }

  const onRequireSequentialCompletionChange = async (value: boolean) => {
    await seriesService.savePriorRequirementRequired(
      new SavePriorRequirementRequiredRequest({
        seriesId: series?.id,
        priorRequirementRequired: value,
      })
    );
    checkedActions.setValue(value);
    queryClient.invalidateQueries("seriesById");
  };

  const rows = [
    {
      title: t({ id: "menuFormNameLabel" }),
      component: series?.seriesTitle && <span>{series.seriesTitle}</span>,
    },
    {
      title: t({ id: "menuFormDescriptionLabel" }),
      component: series?.description && <span>{series.description}</span>,
    },
    {
      title: t({ id: "menuFormRequireSequentialCompletionLabel" }),
      component: (
        <Switch
          checked={checked}
          color="primary"
          onChange={(_e, value) => {
            onRequireSequentialCompletionChange(value);
          }}
        />
      ),
    },
  ].filter((r) => !!r.component);

  return (
    <>
      <Box my={2}>
        <RedirectBackButton
          redirectRoute={seriesDetailsUrl(id)}
          labelKey="backToDetailsButtonLabel"
        />
      </Box>

      <h1>{title}</h1>

      <Typography variant="h5" sx={styles.tableTitle}>
        <T id="seriesLabel" />
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {rows.map(({ title, component }, i) => (
              <TableRow key={i}>
                <TableCell
                  component="th"
                  scope="row"
                  style={{ fontWeight: "bold" }}
                >
                  {title}
                </TableCell>
                <TableCell>{component}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ModulesSection series={series!} mode={Mode.Update} />
    </>
  );
};

export default SeriesUpdateForm;
